/**
 * Block audio recording: start from a block's menu, stop via the global
 * RecordingOverlay. On stop, the recording becomes its own entity in the
 * blocktree — a child block of the host note that carries the asset
 * attributes per docs/schema/asset.md (blob/* + media/*), with a plain
 * text block/content as fallback for non-audio-aware clients.
 *
 * Capture uses @wovin/record's MediaRecorder path (webm/ogg opus) — simple
 * and sufficient for voice notes; the WebCodecs/worklet pipeline can be
 * swapped in later for live-transcription use cases.
 */

import { Logger } from 'besonders-logger'
import { createMediaRecorderStream, getDefaultMediaRecorderConfig, type MediaRecorderStream, requestAudioAccess } from '@wovin/record'
import { createSignal } from 'solid-js'
import type { EntityID } from '@wovin/core/applog'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import { insertApplogs } from '../data/ApplogDB'
import { addBlock } from '../data/block-utils'
import { createUnixFS, retryPendingBlobUploads, saveBlobToOpfs, uploadBlob } from './blob-upload'

const { LOG, WARN, DEBUG } = Logger.setup(Logger.DEBUG, { prefix: '[recorder]' })

export interface ActiveRecording {
	hostBlockId: EntityID
	/** The audio block — created immediately at record-start so it's a real
	 * entity (copyable ID, movable while recording); applogs land on it at stop. */
	recordingBlockId: EntityID
	startedAt: number
}

/** m:ss for elapsed-time displays (RecordingOverlay, RecordingIndicator) */
export function formatElapsed(ms: number): string {
	const totalSec = Math.floor(ms / 1000)
	const min = Math.floor(totalSec / 60)
	const sec = totalSec % 60
	return `${min}:${sec.toString().padStart(2, '0')}`
}

interface RecorderInternals {
	thread: ThreadOnlyCurrentNoDeleted
	stream: MediaStream
	recorder: MediaRecorderStream
	chunks: Blob[]
}

const [activeRecording, setActiveRecording] = createSignal<ActiveRecording | null>(null)
export { activeRecording }
let internals: RecorderInternals | null = null

/** Start recording a voice note — the audio block is created immediately as a
 * child of `hostBlockId` (real entity: copy its ID, move it, while recording). */
export async function startBlockRecording(hostBlockId: EntityID, thread: ThreadOnlyCurrentNoDeleted) {
	if (internals) {
		WARN('Recording already in progress, ignoring start')
		return
	}
	// Mic access first — no orphan block when permission is denied.
	// Content starts as the note3-idiomatic `+recording` tag — the user's
	// editable title continues after it (typable even while recording); the
	// audio presentation itself comes from the asset attrs + player UI.
	const stream = await requestAudioAccess('microphone')
	const recordingBlockId = addBlock({ thread, asChildOf: hostBlockId, content: '+recording' })
	const chunks: Blob[] = []
	const recorder = createMediaRecorderStream(stream, getDefaultMediaRecorderConfig(), (data) => {
		chunks.push(data)
	})
	internals = { thread, stream, recorder, chunks }
	recorder.start()
	setActiveRecording({ hostBlockId, recordingBlockId, startedAt: Date.now() })
	LOG('Recording started for block', hostBlockId, '→ audio block', recordingBlockId)
}

async function waitForRecorderFlush(recorder: MediaRecorderStream) {
	// MediaRecorder delivers the final chunk asynchronously after stop()
	for (let i = 0; i < 20 && recorder.isRecording(); i++) {
		await new Promise(resolve => setTimeout(resolve, 25))
	}
	await new Promise(resolve => setTimeout(resolve, 50))
}

/**
 * Stop the active recording. Unless cancelled, creates the audio block:
 * persists bytes to OPFS, computes the CID locally, inserts the entity
 * applogs, and uploads the CAR opportunistically (queued for retry offline).
 */
export async function stopBlockRecording(cancel = false): Promise<EntityID | null> {
	const active = activeRecording()
	if (!active || !internals) {
		WARN('No active recording to stop')
		return null
	}
	const { thread, stream, recorder, chunks } = internals

	recorder.stop()
	await waitForRecorderFlush(recorder)
	stream.getTracks().forEach(track => track.stop())
	internals = null
	setActiveRecording(null)

	const blockId = active.recordingBlockId

	if (cancel) {
		LOG('Recording cancelled, tombstoning block', blockId)
		insertApplogs(thread, [{ en: blockId, at: 'isDeleted', vl: true }])
		return null
	}

	const durationMs = Date.now() - active.startedAt
	const fullMimeType = recorder.getMimeType() // e.g. 'audio/webm;codecs=opus'
	const mimeType = fullMimeType.split(';')[0].trim()
	const audioBlob = new Blob(chunks, { type: mimeType })
	if (!audioBlob.size) {
		WARN('Recording produced no data, tombstoning block', blockId)
		insertApplogs(thread, [{ en: blockId, at: 'isDeleted', vl: true }])
		return null
	}
	DEBUG('Recording stopped', { durationMs, mimeType, size: audioBlob.size })

	const unixfs = await createUnixFS(new Uint8Array(await audioBlob.arrayBuffer()))
	const cidStr = unixfs.rootCid.toString()
	await saveBlobToOpfs(cidStr, audioBlob).catch(e => WARN('OPFS persist failed (continuing):', e))

	// The block (created at record-start) becomes the asset entity via the
	// asset attributes (attributes-as-perspectives, see asset.md). No label is
	// written — block/content remains the user's editable title; duration/date
	// render from media/* in the player UI.
	const recordedAt = active.startedAt
	insertApplogs(thread, [
		{ en: blockId, at: 'blob/cid', vl: cidStr },
		{ en: blockId, at: 'blob/mimeType', vl: mimeType },
		{ en: blockId, at: 'blob/size', vl: audioBlob.size },
		{ en: blockId, at: 'media/duration', vl: durationMs },
		{ en: blockId, at: 'media/createdAt', vl: recordedAt },
	])
	LOG('Finalized audio block', blockId, { cid: cidStr })

	void uploadBlob(unixfs, mimeType).then(() => retryPendingBlobUploads())

	return blockId
}
